(() => {
  if (window.haircoverWandPage) {
    window.haircoverWandPage.init();
    return;
  }
  const instances = new WeakMap();
  const boot = root => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const observers = [];
    const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: controller.signal });
    const observe = (observer, target) => { observers.push(observer); observer.observe(target); };
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => motion.matches ? 'instant' : 'smooth';
    const animations = [...root.querySelectorAll('[data-motion]')].map(box => {
      const button = box.querySelector('button');
      const setPaused = paused => {
        box.classList.toggle('is-paused', paused);
        button.textContent = paused ? 'Play animation' : 'Pause animation';
        button.setAttribute('aria-pressed', String(paused));
      };
      setPaused(motion.matches);
      listen(button, 'click', () => setPaused(!box.classList.contains('is-paused')));
      return setPaused;
    });

    const slides = root.querySelector('[data-gallery-track]');
    const thumbs = [...root.querySelectorAll('[data-thumb]')];
    const thumbRail = root.querySelector('.hcw-thumbs');
    let current = 0, galleryWidth = slides.clientWidth;
    const syncGallery = () => {
      if (!galleryWidth || Math.abs(slides.clientWidth - galleryWidth) > 1) return;
      current = Math.max(0, Math.min(thumbs.length - 1, Math.round(slides.scrollLeft / galleryWidth)));
      thumbs.forEach((button, i) => button.setAttribute('aria-current', String(i === current)));
      root.querySelector('[data-gallery-count]').textContent = `${current + 1} / ${thumbs.length}`;
    };
    const showThumb = index => {
      const thumbRect = thumbs[index].getBoundingClientRect(), railRect = thumbRail.getBoundingClientRect();
      if (thumbRect.left < railRect.left || thumbRect.right > railRect.right) {
        thumbRail.scrollTo({ left: thumbRail.scrollLeft + thumbRect.left - railRect.left - (railRect.width - thumbRect.width) / 2, behavior: behavior() });
      }
    };
    const goGallery = index => {
      index = (index + thumbs.length) % thumbs.length;
      slides.scrollTo({ left: index * galleryWidth, behavior: behavior() });
      showThumb(index);
    };
    listen(slides, 'scroll', syncGallery, { passive: true });
    thumbs.forEach((button, i) => listen(button, 'click', () => goGallery(i)));
    listen(root.querySelector('[data-gallery-prev]'), 'click', () => goGallery(current - 1));
    listen(root.querySelector('[data-gallery-next]'), 'click', () => goGallery(current + 1));
    listen(slides, 'keydown', event => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      goGallery(event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    observe(new ResizeObserver(() => {
      const width = slides.clientWidth;
      if (!width || Math.abs(width - galleryWidth) < 1) return;
      galleryWidth = width;
      slides.scrollTo({ left: current * width, behavior: 'instant' });
      syncGallery();
      showThumb(current);
    }), slides);
    syncGallery();

    const videos = [...root.querySelectorAll('[data-video] video')];
    const pauseVideos = except => videos.forEach(video => { if (video !== except && !video.paused) video.pause(); });
    videos.forEach(video => {
      const box = video.closest('[data-video]');
      const play = box.querySelector('[data-video-play]');
      const error = box.querySelector('.hcw-video-error');
      play.hidden = false;
      listen(play, 'click', async () => {
        error.hidden = true;
        play.disabled = true;
        try { await video.play(); }
        catch { error.hidden = false; }
        finally { play.disabled = false; }
      });
      listen(video, 'play', () => { pauseVideos(video); play.hidden = true; });
      listen(video, 'pause', () => { play.hidden = false; });
      listen(video, 'ended', () => { play.hidden = false; });
      listen(video, 'error', () => { error.hidden = false; play.hidden = true; });
      observe(new IntersectionObserver(([entry]) => {
        if (entry.intersectionRatio < .15 && !video.paused) video.pause();
      }, { threshold: [0, .15] }), video);
    });
    listen(document, 'visibilitychange', () => { if (document.hidden) pauseVideos(); });

    root.querySelectorAll('[role=tablist]').forEach(list => {
      const tabs = [...list.querySelectorAll('[role=tab]')];
      const selectTab = selected => tabs.forEach(button => {
        const active = button === selected;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        const panel = root.querySelector('#' + button.getAttribute('aria-controls'));
        panel.hidden = !active;
        if (!active) panel.querySelectorAll('video').forEach(video => video.pause());
      });
      tabs.forEach((button, i) => {
        listen(button, 'click', () => selectTab(button));
        listen(button, 'keydown', event => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          selectTab(tabs[next]);
          tabs[next].focus();
        });
      });
    });

    root.querySelectorAll('[data-carousel]').forEach(carousel => {
      const rail = carousel.querySelector('[data-rail]');
      const controls = carousel.querySelector('[data-carousel-controls]');
      const count = carousel.querySelector('[data-rail-count]');
      const step = () => rail.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).gap || 0);
      const updateRail = () => {
        controls.hidden = rail.scrollWidth <= rail.clientWidth + 4;
        if (count) count.textContent = `${Math.min(rail.children.length, Math.round(rail.scrollLeft / step()) + 1)} / ${rail.children.length}`;
      };
      const move = direction => {
        pauseVideos();
        const max = rail.scrollWidth - rail.clientWidth;
        let left = rail.scrollLeft + direction * step();
        if (left > max + 5) left = rail.scrollLeft >= max - 5 ? 0 : max;
        else if (left < -5) left = max;
        rail.scrollTo({ left: Math.max(0, left), behavior: behavior() });
      };
      listen(rail, 'scroll', updateRail, { passive: true });
      listen(rail, 'keydown', event => {
        if (event.target !== rail || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      });
      listen(carousel.querySelector('[data-rail-prev]'), 'click', () => move(-1));
      listen(carousel.querySelector('[data-rail-next]'), 'click', () => move(1));
      observe(new ResizeObserver(updateRail), rail);
      updateRail();
    });
    listen(motion, 'change', () => { if (motion.matches) animations.forEach(setPaused => setPaused(true)); });

    const config = JSON.parse(root.querySelector('[data-config]').textContent);
    const form = root.querySelector('[data-buy-form]');
    const selector = form.querySelector('[name=id]');
    const buttons = [...root.querySelectorAll('[data-add]')];
    const sticky = root.querySelector('[data-sticky]');
    const mainAdd = root.querySelector('[data-main-add]');
    let stickyFrame;
    const selected = () => config.variants.find(variant => String(variant.id) === String(selector.value)) || config.variants[0];
    const money = cents => new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
    const syncSticky = () => {
      cancelAnimationFrame(stickyFrame);
      stickyFrame = requestAnimationFrame(() => {
        const show = selected().available && mainAdd.getBoundingClientRect().bottom < 0 && root.getBoundingClientRect().bottom > sticky.offsetHeight + 24;
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
      });
    };
    const syncVariant = () => {
      const variant = selected();
      root.dataset.available = String(variant.available);
      root.querySelector('[data-availability]').hidden = variant.available;
      root.querySelectorAll('[data-price]').forEach(element => { element.textContent = money(variant.price); });
      root.querySelectorAll('[data-selected-name]').forEach(element => { element.textContent = variant.public_title || variant.title; });
      buttons.forEach(button => {
        button.disabled = !variant.available;
        button.querySelector('[data-label]').textContent = variant.available ? 'Add to cart' : 'Sold out';
      });
      syncSticky();
    };
    listen(selector, 'change', syncVariant);
    syncVariant();
    listen(form, 'submit', async event => {
      event.preventDefault();
      const variant = selected();
      if (!variant.available || mainAdd.disabled) return;
      buttons.forEach(button => { button.disabled = true; button.querySelector('[data-label]').textContent = 'Adding…'; });
      const status = root.querySelector('[data-status]');
      status.textContent = '';
      try {
        const addItems = [{ id: variant.id, quantity: 1 }];
        let hasOil = false;
        try {
          const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' })).json();
          hasOil = (cart.items || []).some(item => item.variant_id === 67575993368805 && item.properties && item.properties._gift);
        } catch (e) {}
        if (!hasOil) addItems.push({ id: 67575993368805, quantity: 1, properties: { _gift: 'Arqevyn' } });
        await window.hcAddToCart(addItems);
        status.textContent = 'Added to your cart.';
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = error.message;
      } finally {
        if (!controller.signal.aborted) syncVariant();
      }
    });
    listen(window, 'scroll', syncSticky, { passive: true });
    listen(window, 'resize', syncSticky);
    observe(new IntersectionObserver(syncSticky), mainAdd);
    instances.set(root, () => {
      pauseVideos(); controller.abort(); cancelAnimationFrame(stickyFrame);
      observers.forEach(observer => observer.disconnect()); instances.delete(root);
    });
  };
  const init = () => document.querySelectorAll('[data-wand-page]').forEach(boot);
  window.haircoverWandPage = { init };
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-wand-page]').forEach(root => instances.get(root)?.()));
  init();
})();
